import {
  GPS_FIX_MIN_MOVING_SPEED_KMH,
  GPS_FIX_RESYNC_S,
  GPS_FIX_TRUSTED_SPEED_TO_TRACK_RATIO,
  GPS_SPEED_NEVER_MOVING_MIN_M,
  GPS_SPEED_NEVER_MOVING_MIN_RUN_WINDOWS,
  GPS_SPEED_WINDOW_MS,
} from '../../analysis/config/telemetryChecks'
import { KMH_PER_MPS } from '../../utils/speedUnits'
import { MS_PER_S } from '../../utils/timeUnits'
import type { GpsStreamCheck } from './gpsStreamTrust'
import { isUsableGpsFix, metresBetween, type GpsFixReading } from '../gpsFix'

const isMoving = (
  fix: GpsFixReading
): fix is GpsFixReading & { speedKmh: number } =>
  fix.speedKmh !== undefined &&
  Number.isFinite(fix.speedKmh) &&
  fix.speedKmh >= GPS_FIX_MIN_MOVING_SPEED_KMH

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

function ratioBetween(from: GpsFixReading, to: GpsFixReading): number | null {
  const ms = to.timeMs - from.timeMs
  if (ms <= 0 || ms > GPS_FIX_RESYNC_S * MS_PER_S) return null
  if (!isMoving(from) || !isMoving(to)) return null
  const metres = metresBetween(from, to)
  if (metres <= 0) return null
  return (
    (((from.speedKmh + to.speedKmh) / 2 / KMH_PER_MPS) * (ms / MS_PER_S)) /
    metres
  )
}

function windowRatios(fixes: readonly GpsFixReading[]): number[] {
  const ratios: number[] = []
  let end = 0
  for (let start = 0; start < fixes.length; start++) {
    if (end <= start) end = start + 1
    while (
      end < fixes.length &&
      fixes[end].timeMs - fixes[start].timeMs < GPS_SPEED_WINDOW_MS
    )
      end++
    if (end >= fixes.length) break
    const ratio = ratioBetween(fixes[start], fixes[end])
    if (ratio !== null) ratios.push(ratio)
  }
  return ratios
}

function consecutiveRatios(fixes: readonly GpsFixReading[]): number[] {
  const ratios: number[] = []
  for (let i = 1; i < fixes.length; i++) {
    const ratio = ratioBetween(fixes[i - 1], fixes[i])
    if (ratio !== null) ratios.push(ratio)
  }
  return ratios
}

export function speedToMovementRatio(
  fixes: readonly GpsFixReading[]
): number | null {
  const usable = fixes.filter(isUsableGpsFix)
  const windows = windowRatios(usable)
  const ratios = windows.length > 0 ? windows : consecutiveRatios(usable)
  return ratios.length === 0 ? null : median(ratios)
}

function metresMovedByPosition(fixes: readonly GpsFixReading[]): number {
  let metres = 0
  let run = { windows: 0, metres: 0 }
  const endRun = () => {
    if (run.windows >= GPS_SPEED_NEVER_MOVING_MIN_RUN_WINDOWS)
      metres += run.metres
    run = { windows: 0, metres: 0 }
  }
  let start = 0
  while (start < fixes.length) {
    let end = start + 1
    while (
      end < fixes.length &&
      fixes[end].timeMs - fixes[start].timeMs < GPS_SPEED_WINDOW_MS
    )
      end++
    if (end >= fixes.length) break
    const seconds = (fixes[end].timeMs - fixes[start].timeMs) / MS_PER_S
    const chord = metresBetween(fixes[start], fixes[end])
    const isMovingByPosition =
      seconds <= GPS_FIX_RESYNC_S &&
      (chord / seconds) * KMH_PER_MPS >= GPS_FIX_MIN_MOVING_SPEED_KMH
    if (isMovingByPosition) {
      run.windows++
      run.metres += chord
    } else {
      endRun()
    }
    start = end
  }
  endRun()
  return metres
}

type NeverMoving = 'zeroThroughout' | 'neverMoving' | null

function speedThatNeverMoves(fixes: readonly GpsFixReading[]): NeverMoving {
  const usable = fixes.filter(isUsableGpsFix)
  if (usable.length === 0) return null
  if (!usable.every((fix) => fix.speedKmh !== undefined)) return null
  if (metresMovedByPosition(usable) < GPS_SPEED_NEVER_MOVING_MIN_M) return null
  return usable.every((fix) => fix.speedKmh === 0)
    ? 'zeroThroughout'
    : 'neverMoving'
}

const NEVER_MOVING_REASON: Record<Exclude<NeverMoving, null>, string> = {
  zeroThroughout:
    'Reported speed is 0 throughout, as when a device recorded none, so speed comes from GPS positions',
  neverMoving:
    'Reported speed never shows the movement, so speed comes from GPS positions',
}

const isWithin = (ratio: number, { min, max }: { min: number; max: number }) =>
  ratio >= min && ratio <= max

const neverMovingFinding = (neverMoving: NeverMoving) =>
  neverMoving === null ? null : speedFinding(NEVER_MOVING_REASON[neverMoving])

const speedFinding = (reason: string) =>
  ({
    check: 'speedMatchesMovement',
    level: 'stream',
    aspect: 'gpsSpeed',
    verdict: 'unreliable',
    reason,
  }) as const

export const speedMatchesMovement: GpsStreamCheck = {
  id: 'speedMatchesMovement',
  run(fixes, trust) {
    if (!trust.timeTrusted)
      return { trust: { ...trust, speedTrusted: false }, finding: null }
    const ratio = speedToMovementRatio(fixes)
    if (ratio === null)
      return {
        trust: { ...trust, speedTrusted: false },
        finding: neverMovingFinding(speedThatNeverMoves(fixes)),
      }
    if (isWithin(ratio, GPS_FIX_TRUSTED_SPEED_TO_TRACK_RATIO))
      return { trust, finding: null }
    return {
      trust: { ...trust, speedTrusted: false },
      finding: speedFinding(
        `Reported speed is ${ratio.toFixed(2)} of the movement, so speed comes from GPS positions`
      ),
    }
  },
}
